<template>
  <div>
    <h3 class="upload-files-header mt-3 mb-3">
      Upload Content files
    </h3>
    <span>
      <base-help-tooltip
        popover-placement="right"
        class="gutters-0 new-gutters-xs"
      >
        <template v-slot:popover-body>
          <p class="mb-0">
            Allowed filetypes ".amr, .flac, .jpeg, .m4a, .mp3, .mp4, .ogg, .pdf, .png, .tiff, .webm, .wav" <br><br>
            File cannot exceed 10MB
          </p>
        </template>
      </base-help-tooltip>
    </span>
    <div class="mt-2">
      <b-form-group
        label-for="spreadsheet-urls-upload-box"
      >
        <div class="custom-file">
          <input
            ref="file"
            :name="fileName"
            accept=".amr, .flac, .jpeg, .m4a, .mp3, .mp4, .ogg, .pdf, .png, .tiff, .webm, .wav"
            style="display:none"
            type="file"
            @cancel="$emit('cancel')"
            @click="resetFile()"
            @change="onFileChange($event.target.files)"
          >
          <label
            class="custom-file-label text-truncate"
            :class="{'disabled': isUploadValid}"
            @click="launchFilePicker()"
          >{{ showFileName }}</label>
        </div>
        <label
          class="main-input-container mt-3"
          for="landing-page-url"
        >Add Landing Page URL</label>
        <b-form-input
          id="landing-page-url"
          :disabled="isUploadValid"
          :state="isValidLandingPage"
          v-model="landingPage"
          class="form-control"
          pattern="https?://.+"
          placeholder="https://www.landing-page.com"
          type="url"
        />
        <b-form-invalid-feedback
          id="input-live-feedback"
          data-test="content-item-url-upload-input-invalid-feedback"
        >
          Please enter a valid Url
        </b-form-invalid-feedback>
      </b-form-group>
      <div class="text-right mb-4">
        <button
          class="btn btn-primary btn-sm"
          :disabled="idAddFileDisabled"
          @click="addFile()"
        >
          Add File
        </button>
      </div>
      <div>
        <b-table
          v-if="uploadedFileList.length >= 1"
          class="card-table table-vcenter text-nowrap table-borderless"
          :striped="false"
          :bordered="false"
          :hover="true"
          :outlined="false"
          :items="uploadedFileList"
          :fields="tableHeaders"
        >
          <template
            v-slot:cell(fileName)="{item}"
          >
            {{ item.fileName }}
          </template>
          <template
            v-slot:cell(landingPage)="{item}"
          >
            {{ item.landingPage }}
          </template>
          <template
            v-slot:cell(bin)="{item}"
          >
            <b-button
              size="md"
              variant="danger"
              class="bin-btn"
              @click="deleteFileFromList(item.fileName)"
            >
              <i class="fa fa-trash-can" />
            </b-button>
          </template>
        </b-table>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';

import helper from '@/core/helper';
import urlHelper from '@/core/mixins/urlHelper';
import BaseHelpTooltip from '@/components/BaseHelpTooltip.vue';
import * as contentTypes from '@/modules/content/store/types';

function initialData() {
  return {
    landingPage: '',
    fileName: '',
    uploadedFile: null,
    uploadedFileList: [],
  };
}

export default {
  name: 'UploadContentFilesTab',
  components: { BaseHelpTooltip },
  mixins: [
    urlHelper,
  ],
  data() {
    return initialData();
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
    ]),
    ...mapState('content', [
      'scrapingBatchId',
      'feedId',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_NEW_CAMPAIGN,
    ]),
    isTabTouched() {
      return this.uploadedFile !== null || this.landingPage !== '' || this.uploadedFileList.length > 0;
    },
    idAddFileDisabled() {
      return this.uploadedFileList.length > 0 || this.uploadedFile === null || !this.isValidLandingPage;
    },
    isValidLandingPage() {
      if (!this.landingPage) return;
      return helper.validURL(this.removeUrlTrailingSlash(this.landingPage));
    },
    isUploadValid() {
      return this.uploadedFileList.length > 0;
    },
    showFileName() {
      if (this.fileName === '') return 'audio_file_chosen.mp3';
      return this.fileName;
    },
    /* eslint-disable object-curly-newline */
    tableHeaders() {
      return [
        { key: 'fileName', label: 'File Name', tdClass: 'ellipsis-column file-name-column' },
        { key: 'landingPage', label: 'Landing Page URL', tdClass: 'ellipsis-column' },
        { key: 'bin', label: '', tdClass: 'text-right', thStyle: { width: '70px' } },
      ];
    },
  },
  methods: {
    ...mapActions('content', [
      contentTypes.SCRAPE_FILE_CONTENT,
      contentTypes.SET_CONTENT_ITEMS_URLS_CHANGED,
    ]),
    addFile() {
      this.uploadedFileList.push({
        fileName: this.fileName,
        landingPage: this.landingPage,
        file: this.uploadedFile,
      });
      this.fileName = '';
      this.landingPage = '';
      this.uploadedFile = null;
    },
    launchFilePicker() {
      if (!this.isUploadValid) {
        this.$refs.file.click();
      }
    },
    onFileChange(files) {
      if (files[0]) {
        this.uploadedFile = files[0];
        this.fileName = files[0].name;
      }
    },
    deleteFileFromList(fileName) {
      this.uploadedFileList = this.uploadedFileList.filter((file) => file.fileName !== fileName);
    },
    resetFile() {
      this.$refs.file.value = '';
    },
    resetData() {
      Object.assign(this.$data, initialData());
    },
    async upload() {
      if (!this.isUploadValid) return;
      this.SET_CONTENT_ITEMS_URLS_CHANGED(true);
      try {
        const payload = {
          advertiserId: this.campaign.advertiserId,
          campaignId: this.campaign.id,
          feedId: this.feedId,
          contentScrapeId: this.scrapingBatchId !== 0 ? this.scrapingBatchId : undefined,
          file: this.uploadedFileList[0].file,
          landingUrl: this.uploadedFileList[0].landingPage,
        };
        await this.SCRAPE_FILE_CONTENT(payload);
        this.$emit('closeModal', { fetchArticles: true });
      } catch (error) {
        this.$emit('uploadIsFailed');
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.disabled {
  cursor: not-allowed;
}
.upload-files-header {
  display: inline-block;
}
.popover-body {
  padding: 0;
}
.table {
  table-layout: fixed;
  width: 100%;
}
::v-deep {
  .ellipsis-column {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
